import React, { useEffect, useState } from 'react';
import { MobileLine } from './types/MobileLine';
import LinhaCard from './components/LinhaCard';
import Modal from './components/Modal';
import VincularCpfModal from './components/VincularCpfModal';
import PaginatedColumnView from './components/PaginatedColumnView';
import AdvancedFilterPanel, { FilterState } from './components/AdvancedFilterPanel';
import Navbar from './components/Navbar';
import api from './services/api';
import { showSuccess, showError, showWarning } from './utils/toastUtils';

const MainPage: React.FC = () => {
  const [mobileLines, setMobileLines] = useState<MobileLine[]>([]);
  const [filteredLines, setFilteredLines] = useState<MobileLine[]>([]);
  const [currentFilters, setCurrentFilters] = useState<FilterState | null>(null);
  const [novoNumero, setNovoNumero] = useState('');
  const [linhaParaVincular, setLinhaParaVincular] = useState<string | null>(null);
  const [isVincularModalOpen, setIsVincularModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [buttonPosition, setButtonPosition] = useState({ x: 16, y: 50 }); // Posição do botão arrastável (em %)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isDragging, setIsDragging] = useState(false);
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalMessage, setModalMessage] = useState('');
  const [modalOnConfirm, setModalOnConfirm] = useState<(() => void) | undefined>(undefined);
  const [modalShowConfirmButton, setModalShowConfirmButton] = useState(true);
  const [modalShowCancelButton, setModalShowCancelButton] = useState(true);
  
  // Estados para importação em massa
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importText, setImportText] = useState('');
  const [numerosParaImportar, setNumerosParaImportar] = useState<{numero: string, existe: boolean}[]>([]);

  const openModal = (
    title: string,
    message: string,
    onConfirm?: () => void,
    showConfirmButton: boolean = true,
    showCancelButton: boolean = true
  ) => {
    setModalTitle(title);
    setModalMessage(message);
    setModalOnConfirm(() => onConfirm);
    setModalShowConfirmButton(showConfirmButton);
    setModalShowCancelButton(showCancelButton);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalOnConfirm(undefined);
  };

  const handleModalConfirm = () => {
    if (modalOnConfirm) {
      modalOnConfirm();
    }
    closeModal();
  };

  // Carregar linhas
  const loadLines = async () => {
    try {
      setLoading(true);
      const lines = await api.getAllLines();
      setMobileLines(lines);
      setFilteredLines(lines);
    } catch (error) {
      console.error('Erro ao carregar linhas:', error);
      showError('Erro ao carregar linhas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLines();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Atualizar filtradas quando linhas mudam
  useEffect(() => {
    if (currentFilters === null) {
      setFilteredLines(mobileLines);
    }
  }, [mobileLines, currentFilters]);

  // Handler para filtros avançados
  const handleFilterChange = (filtered: MobileLine[], filters: FilterState) => {
    setFilteredLines(filtered);
    setCurrentFilters(filters);
  };

  // Cadastrar nova linha
  const handleCadastrarLinha = async () => {
    const numeroLimpo = novoNumero.replace(/\D/g, '');
    
    if (!numeroLimpo || numeroLimpo.length < 10) {
      showWarning('Digite um número válido (10 ou 11 dígitos)');
      return;
    }

    // Verifica se já existe
    if (mobileLines.some(l => l.numero === numeroLimpo)) {
      showWarning('Linha já cadastrada');
      return;
    }

    try {
      const novaLinha: Partial<MobileLine> = {
        numero: numeroLimpo,
        status: 'semUsuario'
      };
      
      await api.createLine(novaLinha);
      setNovoNumero('');
      await loadLines();
      showSuccess('Linha cadastrada com sucesso!');
    } catch (error: any) {
      console.error('Erro ao cadastrar linha:', error);
      showError(error.response?.data?.error || 'Erro ao cadastrar linha');
    }
  };

  // Importação em massa de linhas
  // Validar números colados no textarea
  const handleValidarNumeros = () => {
    if (!importText.trim()) {
      showWarning('Cole os números no campo de texto');
      return;
    }

    // Extrair números do texto (formato: (84) 98667-7652 ou outros formatos)
    const linhas = importText.split('\n').map(linha => linha.trim()).filter(Boolean);
    const numerosLimpos = linhas.map(linha => linha.replace(/\D/g, '')).filter(n => n.length >= 10);

    if (numerosLimpos.length === 0) {
      showWarning('Nenhum número válido encontrado');
      return;
    }

    // Verificar quais já existem no banco
    const numerosValidados = numerosLimpos.map(numero => ({
      numero,
      existe: mobileLines.some(l => l.numero === numero)
    }));

    setNumerosParaImportar(numerosValidados);
  };

  // Remover número da lista de importação
  const handleRemoverNumero = (numero: string) => {
    setNumerosParaImportar(prev => prev.filter(n => n.numero !== numero));
  };

  // Importar apenas números não existentes (após validação)
  const handleImportarEmMassa = async () => {
    const numerosNovos = numerosParaImportar.filter(n => !n.existe).map(n => n.numero);

    if (numerosNovos.length === 0) {
      showWarning('Nenhum número novo para cadastrar');
      return;
    }

    // Cadastrar novos números
    setLoading(true);
    const adicionados: string[] = [];
    const erros: string[] = [];

    for (const numero of numerosNovos) {
      try {
        const novaLinha: Partial<MobileLine> = {
          numero,
          status: 'semUsuario'
        };
        await api.createLine(novaLinha);
        adicionados.push(numero);
      } catch (error: any) {
        console.error(`Erro ao cadastrar ${numero}:`, error);
        erros.push(numero);
      }
    }

    setLoading(false);
    await loadLines();
    if (adicionados.length > 0) showSuccess(`${adicionados.length} linha(s) adicionada(s)`);
    if (erros.length > 0) showError(`${erros.length} erro(s) ao cadastrar`);
    setImportText('');
    setNumerosParaImportar([]);
    setIsImportModalOpen(false);
  };

  // Vincular CPF a uma linha
  const handleVincularClick = (numero: string) => {
    setLinhaParaVincular(numero);
    setIsVincularModalOpen(true);
  };

  const handleVincular = async (cpfcnpj: string, contrato: string, clienteData: any) => {
    if (!linhaParaVincular) return;

    try {
      setLoading(true);
      
      // Encontrar contrato selecionado
      const contratoSelecionado = clienteData.contratos.find(
        (c: any) => String(c.contrato ?? c.contratoId ?? c.id ?? c.numeroContrato) === String(contrato)
      );

      const servicoPrincipal = contratoSelecionado?.servicos?.[0];
      const plano = (servicoPrincipal?.plano?.descricao) || contratoSelecionado?.planointernet || '';
      const login = servicoPrincipal?.login || contratoSelecionado?.servico_login || '';
      const senha = servicoPrincipal?.senha || contratoSelecionado?.servico_senha || '';

      // Buscar faturas (usando a senha do contrato quando disponível)
      const faturasResponse = await api.fetchInvoices(cpfcnpj, contrato, contratoSelecionado?.contratoCentralSenha);
      const faturas = faturasResponse.faturas || [];
      const statusCalculado = faturasResponse.statusCalculado || 'adimplente';

      // Atualizar linha
      const dataAgora = new Date().toISOString();
      const linhaAtualizada: Partial<MobileLine> = {
        nome: clienteData.nome,
        cpfCnpj: cpfcnpj,
        contrato: contrato,
        contratoId: parseInt(String(contrato)),
        contratoCentralSenha: contratoSelecionado?.contratoCentralSenha || 'centraldoassinante',
        plano,
        valorMensal: clienteData.valorMensalAdicionado || 0, // Valor inserido no modal
        gigasDisponiveis: clienteData.gigasAdicionado || 0, // Gigas inserido no modal
        loginPPPoE: login,
        senhaPPPoE: senha,
        status: statusCalculado,
        faturas: JSON.stringify(faturas),
        dataAdimplencia: statusCalculado === 'adimplente' ? dataAgora : undefined,
        dataInadimplencia: statusCalculado === 'inadimplente' ? dataAgora : undefined,
      };

      await api.updateLine(linhaParaVincular, linhaAtualizada);
      
      // Resetar o status de liberação/bloqueio para que os avisos apareçam novamente
      if (statusCalculado === 'adimplente') {
        await api.updateLineStatus(linhaParaVincular, false, false); // Reset: liberado=false para exibir aviso
      } else if (statusCalculado === 'inadimplente') {
        await api.updateLineStatus(linhaParaVincular, false, false); // Reset: bloqueado=false para exibir aviso
      }
      
      await loadLines();
      
      showSuccess(`Linha vinculada ao contrato ${contrato}!`);
    } catch (error: any) {
      console.error('Erro ao vincular linha:', error);
      showError(error.response?.data?.error || 'Erro ao vincular linha');
    } finally {
      setLoading(false);
      setLinhaParaVincular(null);
    }
  };

  // Excluir linha
  const handleExcluirLinha = async (numero: string) => {
    openModal(
      'Confirmar Exclusão',
      `Deseja realmente excluir a linha ${numero}?`,
      async () => {
        try {
          await api.deleteLine(numero);
          await loadLines();
          showSuccess('Linha excluída com sucesso!');
        } catch (error) {
          console.error('Erro ao excluir linha:', error);
          showError('Erro ao excluir linha');
        }
      },
      true
    );
  };

  // Atualizar faturas
  const handleAtualizarFaturas = async (numero: string) => {
    const linha = mobileLines.find(l => l.numero === numero);
    if (!linha || !linha.cpfCnpj || !linha.contrato) {
      showWarning('Linha não possui contrato vinculado');
      return;
    }

    try {
      setLoading(true);
      console.log(`[MainPage] Atualizando faturas para linha ${numero}:`, {
        cpfCnpj: linha.cpfCnpj,
        contrato: linha.contrato,
        senha: linha.contratoCentralSenha
      });

      const faturasResponse = await api.fetchInvoices(linha.cpfCnpj, String(linha.contrato), linha.contratoCentralSenha);
      const faturas = faturasResponse.faturas || [];
      const statusCalculado = faturasResponse.statusCalculado || 'adimplente';

      console.log(`[MainPage] Resposta SGP para linha ${numero}:`, {
        faturasCount: faturas.length,
        statusCalculado,
        primeriFatura: faturas[0]
      });

      await api.updateLine(numero, {
        faturas: JSON.stringify(faturas),
        status: statusCalculado,
        dataAdimplencia: statusCalculado === 'adimplente' ? new Date().toISOString() : linha.dataAdimplencia,
        dataInadimplencia: statusCalculado === 'inadimplente' ? new Date().toISOString() : linha.dataInadimplencia,
      });

      // Resetar o status de liberação/bloqueio para que os avisos apareçam novamente
      if (statusCalculado === 'adimplente') {
        await api.updateLineStatus(numero, false, false); // Reset: liberado=false para exibir aviso
      } else if (statusCalculado === 'inadimplente') {
        await api.updateLineStatus(numero, false, false); // Reset: bloqueado=false para exibir aviso
      }

      await loadLines();
      showSuccess('Faturas atualizadas!');
    } catch (error) {
      console.error('Erro ao atualizar faturas:', error);
      showError('Erro ao atualizar faturas');
    } finally {
      setLoading(false);
    }
  };

  // Remover vínculo (desvincular contrato/cliente SGP)
  const handleRemoverVinculo = async (numero: string) => {
    openModal(
      'Remover Vínculo',
      `Deseja remover o vínculo (CPF/Contrato) da linha ${numero}?`,
      async () => {
        try {
          setLoading(true);
          await api.unlinkLine(numero);
          await loadLines();
          showSuccess('Vínculo removido. Linha voltou para Sem Usuário.');
        } catch (error: any) {
          console.error('Erro ao remover vínculo:', error);
          const msg = error?.response?.data?.error || 'Não foi possível remover o vínculo';
          showError(msg);
        } finally {
          setLoading(false);
        }
      },
      true
    );
  };

  // Atualizar campos da linha (plano, valor, etc)
  const handleAtualizarLinha = async (numero: string, data: Partial<MobileLine>, silencioso: boolean = false) => {
    try {
      setLoading(true);
      await api.updateLine(numero, data);
      await loadLines();
      if (!silencioso) {
        showSuccess('Linha atualizada com sucesso!');
      }
    } catch (error) {
      console.error('Erro ao atualizar linha:', error);
      if (!silencioso) {
        showError('Erro ao atualizar linha');
      }
    } finally {
      setLoading(false);
    }
  };

  // Função de filtro por busca (busca em TODAS as linhas, inclusive semUsuario)
  const filterBySearch = (linha: MobileLine) => {
    if (!searchTerm) return true;
    
    const term = searchTerm.toLowerCase();
    const numero = linha.numero.toLowerCase();
    const contrato = linha.contrato?.toLowerCase() || '';
    const nome = linha.nome?.toLowerCase() || '';
    
    return numero.includes(term) || contrato.includes(term) || nome.includes(term);
  };

  // Verificar se a busca encontrou algo em "Sem Usuário" (para abrir sidebar automaticamente)
  const semUsuarioAll = mobileLines.filter((l) => l.status === 'semUsuario');
  const semUsuarioFiltered = semUsuarioAll.filter(filterBySearch);
  const searchFoundInSemUsuario = searchTerm && semUsuarioFiltered.length > 0;

  // Filtrar linhas por status e busca
  const semUsuario = semUsuarioFiltered;
  
  // Separar inadimplentes em Bloqueados (confirmados) e Inadimplentes (pendentes)
  const todasInadimplentes = filteredLines.filter((l) => l.status === 'inadimplente' && filterBySearch(l));
  
  // Bloqueados: linhas com aviso de bloqueio CONFIRMADO, ordenados por data (mais antigos primeiro)
  const bloqueados = todasInadimplentes
    .filter((l) => l.bloqueioConfirmado === true)
    .sort((a, b) => {
      const dataA = a.dataBloqueio ? new Date(a.dataBloqueio).getTime() : 0;
      const dataB = b.dataBloqueio ? new Date(b.dataBloqueio).getTime() : 0;
      return dataA - dataB; // Mais antigos primeiro
    });
  
  // Inadimplentes: linhas SEM confirmação de bloqueio (pendentes)
  const inadimplentes = todasInadimplentes.filter((l) => !l.bloqueioConfirmado);
  
  const adimplentes = filteredLines.filter((l) => l.status === 'adimplente' && filterBySearch(l));

  // Abrir sidebar automaticamente se busca encontrou algo em "Sem Usuário"
  useEffect(() => {
    if (searchFoundInSemUsuario) {
      setSidebarOpen(true);
    }
  }, [searchFoundInSemUsuario]);

  // Calcular estatísticas usando TODAS as linhas do banco (não filtradas)
  const todasAdimplentes = mobileLines.filter((l) => l.status === 'adimplente');
  const todasInadimplentesReal = mobileLines.filter((l) => l.status === 'inadimplente');
  const linhasAtivasReal = [...todasInadimplentesReal, ...todasAdimplentes];
  
  // Valores por status (do banco completo)
  const valorAdimplentes = todasAdimplentes.reduce((sum, linha) => sum + (linha.valorMensal || 0), 0);
  const valorInadimplentes = todasInadimplentesReal.reduce((sum, linha) => sum + (linha.valorMensal || 0), 0);
  const valorTotalArrecadado = valorAdimplentes + valorInadimplentes;

  // Calcular total de gigas disponíveis (do banco completo)
  const totalGigas = linhasAtivasReal.reduce((sum, linha) => {
    return sum + (linha.gigasDisponiveis || 0);
  }, 0);
  
  // Contadores para exibição nos cards das estatísticas
  const countAdimplentes = todasAdimplentes.length;
  const countInadimplentes = todasInadimplentesReal.length;
  const countLinhasAtivas = linhasAtivasReal.length;

  return (
    <div className="min-h-screen bg-zinc-900 text-white">
      {/* Navbar */}
      <Navbar currentPage="lines" />

      {/* Painel de Filtros Avançados */}
      <div className="px-4 pt-4">
        <AdvancedFilterPanel lines={mobileLines} onFilterChange={handleFilterChange} />
      </div>

      {/* Formulário de Cadastro e Busca */}
      <div className="p-4 bg-zinc-800 m-4 rounded-xl shadow-lg">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Cadastrar Nova Linha */}
          <div>
            <h2 className="text-xl font-bold text-green-400 mb-4">Cadastrar Nova Linha</h2>
            <div className="flex gap-2">
              <input
                type="text"
                value={novoNumero}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '');
                  if (value.length <= 11) {
                    const formatted = value.length <= 10
                      ? value.replace(/(\d{2})(\d{0,4})(\d{0,4})/, (_, ddd, p1, p2) => {
                          let result = ddd ? `(${ddd}` : '';
                          if (p1) result += `) ${p1}`;
                          if (p2) result += `-${p2}`;
                          return result;
                        })
                      : value.replace(/(\d{2})(\d{5})(\d{0,4})/, (_, ddd, p1, p2) => {
                          let result = `(${ddd}) ${p1}`;
                          if (p2) result += `-${p2}`;
                          return result;
                        });
                    setNovoNumero(formatted);
                  }
                }}
                placeholder="Ex: (84) 99999-9999"
                className="flex-1 px-4 py-2 bg-zinc-700 text-white border border-zinc-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                disabled={loading}
                onKeyPress={(e) => e.key === 'Enter' && handleCadastrarLinha()}
                maxLength={15}
              />
              <button
                onClick={handleCadastrarLinha}
                disabled={loading}
                className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white font-bold rounded-md disabled:bg-gray-500"
              >
                {loading ? 'Cadastrando...' : 'Cadastrar'}
              </button>
              <button
                onClick={() => setIsImportModalOpen(true)}
                disabled={loading}
                className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-md disabled:bg-gray-500"
                title="Importar múltiplas linhas"
              >
                📋 Massa
              </button>
            </div>
          </div>


          {/* Buscar Linha */}
          <div>
            <h2 className="text-xl font-bold text-blue-400 mb-4">Buscar Linha</h2>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar por número, contrato ou cliente..."
                  className="w-full px-4 py-2 bg-zinc-700 text-white border border-zinc-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 pl-10"
                />
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                  🔍
                </span>
              </div>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white font-bold rounded-md"
                  title="Limpar busca"
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Estatísticas */}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Valor Total Arrecadado */}
          <div className="bg-gradient-to-br from-green-600 to-green-700 p-4 rounded-lg shadow-md hover:shadow-xl transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-xs font-medium">💰 TOTAL A RECEBER</p>
                <p className="text-white text-2xl font-bold mt-1">
                  R$ {valorTotalArrecadado.toFixed(2)}
                </p>
                <p className="text-green-200 text-xs mt-1">
                  {countLinhasAtivas} linha{countLinhasAtivas !== 1 ? 's' : ''} ativa{countLinhasAtivas !== 1 ? 's' : ''}
                </p>
              </div>
              <div className="text-4xl opacity-20">💵</div>
            </div>
          </div>

          {/* Valor Adimplentes */}
          <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 p-4 rounded-lg shadow-md hover:shadow-xl transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-100 text-xs font-medium">✓ ADIMPLENTES</p>
                <p className="text-white text-2xl font-bold mt-1">
                  R$ {valorAdimplentes.toFixed(2)}
                </p>
                <p className="text-emerald-200 text-xs mt-1">
                  {countAdimplentes} linha{countAdimplentes !== 1 ? 's' : ''}
                </p>
              </div>
              <div className="text-4xl opacity-20">✅</div>
            </div>
          </div>

          {/* Valor Inadimplentes */}
          <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-4 rounded-lg shadow-md hover:shadow-xl transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-xs font-medium">⚠️ INADIMPLENTES</p>
                <p className="text-white text-2xl font-bold mt-1">
                  R$ {valorInadimplentes.toFixed(2)}
                </p>
                <p className="text-orange-200 text-xs mt-1">
                  {countInadimplentes} linha{countInadimplentes !== 1 ? 's' : ''}
                </p>
              </div>
              <div className="text-4xl opacity-20">⚠️</div>
            </div>
          </div>

          {/* Total de Gigas */}
          <div className="bg-gradient-to-br from-blue-600 to-blue-700 p-4 rounded-lg shadow-md hover:shadow-xl transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-xs font-medium">📊 TOTAL GIGAS</p>
                <p className="text-white text-2xl font-bold mt-1">
                  {totalGigas.toFixed(0)} GB
                </p>
                <p className="text-blue-200 text-xs mt-1">
                  Soma de todas as linhas
                </p>
              </div>
              <div className="text-4xl opacity-20">📡</div>
            </div>
          </div>
        </div>
      </div>

      {/* Sidebar Retrátil - Sem Usuário */}
      <div
        className={`fixed top-0 left-0 h-full bg-zinc-800 border-r border-yellow-500 shadow-2xl z-40 transition-all duration-300 ${
          sidebarOpen ? 'w-80' : 'w-0'
        } overflow-hidden`}
      >
        <div className="w-80 h-full flex flex-col">
          <div className="p-4 border-b border-zinc-700 flex justify-between items-center">
            <h2 className="text-yellow-400 font-bold text-xl">
              📦 Sem Usuário ({semUsuario.length})
            </h2>
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-gray-400 hover:text-white text-2xl"
            >
              ✕
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-2">
            {semUsuario.map((line) => (
              <LinhaCard
                key={line.numero}
                line={line}
                onVincular={() => handleVincularClick(line.numero)}
                onExcluir={handleExcluirLinha}
                onAtualizarFaturas={handleAtualizarFaturas}
                onRemoverVinculo={(numero) => handleRemoverVinculo(numero)}
                onAtualizarLinha={handleAtualizarLinha}
                openModal={openModal}
              />
            ))}
            {semUsuario.length === 0 && (
              <p className="text-gray-400 text-center py-4">Nenhuma linha sem usuário</p>
            )}
          </div>
        </div>
      </div>

      {/* Overlay quando sidebar está aberta */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Botão Flutuante Arrastável para abrir sidebar */}
      <div
        className={`fixed z-20 cursor-move select-none transition-opacity ${
          sidebarOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'
        }`}
        style={{ 
          left: `${buttonPosition.x}px`, 
          top: `${buttonPosition.y}%`,
          transform: 'translateY(-50%)'
        }}
        onMouseDown={(e) => {
          // Verificar se clicou em elemento clicável (emoji ou número)
          const target = e.target as HTMLElement;
          if (target.dataset.clickable === 'true') {
            return; // Não arrastar, deixar o onClick funcionar
          }
          
          e.preventDefault();
          setIsDragging(true);
          const startX = e.clientX - buttonPosition.x;
          const startY = e.clientY;
          const startPercent = buttonPosition.y;
          
          const handleMouseMove = (moveEvent: MouseEvent) => {
            const newX = Math.max(0, Math.min(window.innerWidth - 100, moveEvent.clientX - startX));
            const deltaY = moveEvent.clientY - startY;
            const newYPercent = Math.max(10, Math.min(90, startPercent + (deltaY / window.innerHeight) * 100));
            setButtonPosition({ x: newX, y: newYPercent });
          };
          
          const handleMouseUp = () => {
            setIsDragging(false);
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
          };
          
          document.addEventListener('mousemove', handleMouseMove);
          document.addEventListener('mouseup', handleMouseUp);
        }}
        onTouchStart={(e) => {
          // Verificar se tocou em elemento clicável (emoji ou número)
          const target = e.target as HTMLElement;
          if (target.dataset.clickable === 'true') {
            return; // Não arrastar, deixar o onClick funcionar
          }
          
          const touch = e.touches[0];
          setIsDragging(true);
          const startX = touch.clientX - buttonPosition.x;
          const startY = touch.clientY;
          const startPercent = buttonPosition.y;
          
          const handleTouchMove = (moveEvent: TouchEvent) => {
            const moveTouch = moveEvent.touches[0];
            const newX = Math.max(0, Math.min(window.innerWidth - 100, moveTouch.clientX - startX));
            const deltaY = moveTouch.clientY - startY;
            const newYPercent = Math.max(10, Math.min(90, startPercent + (deltaY / window.innerHeight) * 100));
            setButtonPosition({ x: newX, y: newYPercent });
          };
          
          const handleTouchEnd = () => {
            setIsDragging(false);
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('touchend', handleTouchEnd);
          };
          
          document.addEventListener('touchmove', handleTouchMove);
          document.addEventListener('touchend', handleTouchEnd);
        }}
      >
        <div className="bg-yellow-600 text-white font-bold py-3 px-4 rounded-lg shadow-lg flex items-center gap-2 cursor-move">
          {/* Emoji - clicável para abrir */}
          <span 
            data-clickable="true"
            className="text-lg cursor-pointer hover:scale-110 transition-transform"
            onClick={() => setSidebarOpen(true)}
            title="Clique para abrir"
          >
            📦
          </span>
          {/* Número - clicável para abrir */}
          <span 
            data-clickable="true"
            className="bg-yellow-800 px-2 py-0.5 rounded-full text-sm cursor-pointer hover:bg-yellow-900 transition-colors"
            onClick={() => setSidebarOpen(true)}
            title="Clique para abrir"
          >
            {semUsuario.length}
          </span>
          {/* Indicador de arrastar */}
          <span className="text-xs opacity-50" title="Arraste para mover">⋮⋮</span>
        </div>
      </div>

      {/* Cards de Linhas - 3 colunas com Paginação */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-4">
        {/* Bloqueados (confirmados) */}
        <PaginatedColumnView
          title="🔒 Bloqueados"
          status="inadimplente"
          confirmed={true}
          lines={bloqueados}
          onVincular={handleVincularClick}
          onAtualizarLinha={handleAtualizarLinha}
          onExcluir={handleExcluirLinha}
          onAtualizarFaturas={handleAtualizarFaturas}
          onRemoverVinculo={handleRemoverVinculo}
          openModal={openModal}
          borderColor="red"
          itemsPerPage={15}
        />

        {/* Inadimplentes (pendentes de bloqueio) */}
        <PaginatedColumnView
          title="⚠️ Inadimplentes"
          status="inadimplente"
          confirmed={false}
          lines={inadimplentes}
          onVincular={handleVincularClick}
          onAtualizarLinha={handleAtualizarLinha}
          onExcluir={handleExcluirLinha}
          onAtualizarFaturas={handleAtualizarFaturas}
          onRemoverVinculo={handleRemoverVinculo}
          openModal={openModal}
          borderColor="orange"
          itemsPerPage={15}
        />

        {/* Adimplentes */}
        <PaginatedColumnView
          title="✅ Adimplentes"
          status="adimplente"
          confirmed={null}
          lines={adimplentes}
          onVincular={handleVincularClick}
          onAtualizarLinha={handleAtualizarLinha}
          onExcluir={handleExcluirLinha}
          onAtualizarFaturas={handleAtualizarFaturas}
          onRemoverVinculo={handleRemoverVinculo}
          openModal={openModal}
          borderColor="green"
          itemsPerPage={15}
        />
      </div>

      {/* Modais */}
      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        onConfirm={modalOnConfirm ? handleModalConfirm : undefined}
        title={modalTitle}
        message={modalMessage}
        showConfirmButton={modalShowConfirmButton}
        showCancelButton={modalShowCancelButton}
      />

      <VincularCpfModal
        isOpen={isVincularModalOpen}
        onClose={() => {
          setIsVincularModalOpen(false);
          setLinhaParaVincular(null);
        }}
        onVincular={handleVincular}
        numeroLinha={linhaParaVincular || ''}
      />

      {/* Modal de Importação em Massa */}
      {isImportModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-800 rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-purple-400">📋 Importar Linhas em Massa</h2>
                <button
                  onClick={() => {
                    setIsImportModalOpen(false);
                    setImportText('');
                    setNumerosParaImportar([]);
                  }}
                  className="text-gray-400 hover:text-white text-2xl"
                >
                  ✕
                </button>
              </div>

              {numerosParaImportar.length === 0 ? (
                <>
                  <p className="text-gray-300 mb-4">
                    Cole os números abaixo, um por linha. Formatos aceitos: (84) 98667-7652, 84986677652, etc.
                  </p>

                  <textarea
                    value={importText}
                    onChange={(e) => setImportText(e.target.value)}
                    placeholder="(84) 99999-9999&#10;(84) 88888-8888&#10;(84) 77777-7777&#10;"
                    className="w-full h-64 px-4 py-2 bg-zinc-700 text-white border border-zinc-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono text-sm"
                    disabled={loading}
                  />

                  <div className="flex gap-3 mt-6">
                    <button
                      onClick={handleValidarNumeros}
                      disabled={loading || !importText.trim()}
                      className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-md disabled:bg-gray-500 disabled:cursor-not-allowed"
                    >
                      🔍 Validar Números
                    </button>
                    <button
                      onClick={() => {
                        setIsImportModalOpen(false);
                        setImportText('');
                      }}
                      disabled={loading}
                      className="px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white font-bold rounded-md disabled:bg-gray-500"
                    >
                      Cancelar
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-gray-300 mb-4">
                    {numerosParaImportar.filter(n => n.existe).length > 0 && (
                      <span className="text-yellow-400">
                        ⚠️ {numerosParaImportar.filter(n => n.existe).length} número(s) já cadastrado(s). 
                      </span>
                    )}
                    {' '}
                    {numerosParaImportar.filter(n => !n.existe).length > 0 && (
                      <span className="text-green-400">
                        ✓ {numerosParaImportar.filter(n => !n.existe).length} número(s) novo(s).
                      </span>
                    )}
                    {' '}Remova os indesejados clicando no ✕
                  </p>

                  <div className="max-h-96 overflow-y-auto bg-zinc-900 rounded-md p-4 space-y-2">
                    {numerosParaImportar.map(({ numero, existe }) => {
                      const formatado = `(${numero.substring(0, 2)}) ${numero.substring(2, numero.length - 4)}-${numero.substring(numero.length - 4)}`;
                      return (
                        <div
                          key={numero}
                          className={`flex items-center justify-between p-3 rounded-md ${
                            existe 
                              ? 'bg-yellow-900/30 border border-yellow-600' 
                              : 'bg-green-900/30 border border-green-600'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">
                              {existe ? '⚠️' : '✅'}
                            </span>
                            <div>
                              <p className="text-white font-mono">{formatado}</p>
                              <p className="text-xs text-gray-400">
                                {existe ? 'Já cadastrado no banco' : 'Novo número'}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleRemoverNumero(numero)}
                            className="text-red-400 hover:text-red-600 text-xl px-2 py-1 rounded hover:bg-red-900/30"
                            title="Remover da lista"
                          >
                            ✕
                          </button>
                        </div>
                      );
                    })}
                  </div>

                  <div className="flex gap-3 mt-6">
                    <button
                      onClick={handleImportarEmMassa}
                      disabled={loading || numerosParaImportar.filter(n => !n.existe).length === 0}
                      className="flex-1 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-md disabled:bg-gray-500 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Importando...' : `✓ Importar ${numerosParaImportar.filter(n => !n.existe).length} Número(s)`}
                    </button>
                    <button
                      onClick={() => {
                        setNumerosParaImportar([]);
                      }}
                      disabled={loading}
                      className="px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white font-bold rounded-md disabled:bg-gray-500"
                    >
                      ← Voltar
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MainPage;
